import type { UnitSystem } from '../model/types';

const METRES_PER_INCH = 0.0254;
const METRES_PER_FOOT = 0.3048;

export function parseLength(input: string, defaultUnit: UnitSystem = 'metric'): number | null {
  const text = input.trim().toLowerCase().replace(/,/g, '');
  if (!text) return null;

  const feetInches = text.match(/^(-?\d+(?:\.\d+)?)\s*(?:ft|')\s*(?:(\d+(?:\.\d+)?)\s*(?:in|"))?$/);
  if (feetInches) {
    const feet = Number(feetInches[1]);
    const inches = Number(feetInches[2] ?? 0);
    return feet * METRES_PER_FOOT + Math.sign(feet || 1) * inches * METRES_PER_INCH;
  }

  const match = text.match(/^(-?\d+(?:\.\d+)?)\s*(mm|cm|m|metres?|meters?|in|inches?|"|ft|feet|')?$/);
  if (!match) return null;

  const value = Number(match[1]);
  const unit = match[2];
  if (!Number.isFinite(value)) return null;

  switch (unit) {
    case 'mm': return value / 1000;
    case 'cm': return value / 100;
    case 'in':
    case 'inch':
    case 'inches':
    case '"': return value * METRES_PER_INCH;
    case 'ft':
    case 'feet':
    case "'": return value * METRES_PER_FOOT;
    case 'm':
    case 'metre':
    case 'metres':
    case 'meter':
    case 'meters': return value;
    default: return defaultUnit === 'imperial' ? value * METRES_PER_FOOT : value;
  }
}

export function formatLength(metres: number, unitSystem: UnitSystem, precision = 2): string {
  if (unitSystem === 'imperial') {
    const totalInches = metres / METRES_PER_INCH;
    const sign = totalInches < 0 ? '-' : '';
    const absolute = Math.abs(totalInches);
    const feet = Math.floor(absolute / 12);
    const inches = absolute - feet * 12;
    return `${sign}${feet}' ${inches.toFixed(precision)}\"`;
  }

  if (Math.abs(metres) < 1) return `${(metres * 1000).toFixed(0)} mm`;
  return `${metres.toFixed(precision)} m`;
}

export function formatArea(squareMetres: number, unitSystem: UnitSystem, precision = 2): string {
  if (unitSystem === 'imperial') return `${(squareMetres * 10.7639104167).toFixed(precision)} ft²`;
  return `${squareMetres.toFixed(precision)} m²`;
}

export function formatVolume(cubicMetres: number, unitSystem: UnitSystem, precision = 2): string {
  if (unitSystem === 'imperial') return `${(cubicMetres * 35.3146667215).toFixed(precision)} ft³`;
  return `${cubicMetres.toFixed(precision)} m³`;
}
